import { useState, type FormEvent } from "react";

type Status = "idle" | "submitting" | "success" | "error";

interface ParsedResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUCCESS_MESSAGE =
  "Thank you — your message is on its way. Eddie's team will be in touch.";
const FALLBACK_ERROR = "Something went wrong on our end. Please try again.";

export function useFormSubmit(endpoint: string) {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot: silently mimic success so bots get no signal.
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage(SUCCESS_MESSAGE);
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    let hasPhotos = false;
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (value instanceof File) {
        if (value.name) hasPhotos = true;
        return;
      }
      const str = String(value).trim();
      if (!str) return;
      payload.append(key, str);
    });
    if (hasPhotos) payload.append("photos", "Uncollectable");

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      const isSpam =
        typeof serverMessage === "string" &&
        serverMessage.toLowerCase().includes("spam");
      const codeOk = parsed?.code === "OK";

      if (response.ok && codeOk && !isSpam) {
        setStatus("success");
        setMessage(SUCCESS_MESSAGE);
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMessage || FALLBACK_ERROR);
      }
    } catch (error) {
      setStatus("error");
      setMessage("We couldn't send that just now. Please try again.");
      void error;
    }
  };

  return { status, message, handleSubmit };
}